import React, { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const CreateBrand: React.FC = () => {
  const { user } = useAuth();
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [team, setTeam] = useState('');
  const [brandStyle, setBrandStyle] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedUrl, setUploadedUrl] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    let fileUrl = null;
    if (brandStyle) {
      setUploading(true);
      const { data, error: uploadError } = await supabase.storage
        .from('brand-styles')
        .upload(`styles/${Date.now()}_${brandStyle.name}`, brandStyle);
      setUploading(false);
      if (uploadError) {
        setError('File upload failed: ' + uploadError.message);
        return;
      }
      fileUrl = data?.path ? supabase.storage.from('brand-styles').getPublicUrl(data.path).data.publicUrl : null;
      setUploadedUrl(fileUrl);
    }
    // Insert into brands table with user_id
    const { error: dbError } = await supabase.from('brands').insert([
      {
        name,
        description: desc,
        industry: team, // Map team to industry field
        logo_url: fileUrl, // Map brand_style_url to logo_url field
        user_id: user?.id,
      },
    ]);
    if (dbError) {
      setError('Failed to create brand: ' + dbError.message);
      return;
    }
    setSuccess(true);
    setTimeout(() => {
      navigate('/dashboard/brands');
    }, 1000);
    setName('');
    setDesc('');
    setTeam('');
    setBrandStyle(null);
    setUploadedUrl(null);
  };

  return (
    <div className="max-w-xl mx-auto bg-white rounded-xl shadow border p-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Create Brand</h1>
      {success && (
        <div className="mb-4 p-4 text-green-700 bg-green-100 rounded-lg">Brand created successfully!</div>
      )}
      {error && (
        <div className="mb-4 p-4 text-red-700 bg-red-100 rounded-lg">{error}</div>
      )}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Brand Name</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <input
            type="text"
            value={desc}
            onChange={e => setDesc(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Team</label>
          <input
            type="text"
            value={team}
            onChange={e => setTeam(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Brand Style (PDF, PNG, JPG)</label>
          <input
            type="file"
            accept=".pdf,.png,.jpg,.jpeg"
            onChange={e => setBrandStyle(e.target.files?.[0] || null)}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-white hover:file:bg-primary/90"
          />
          {brandStyle && (
            <div className="mt-2 text-xs text-gray-500">Selected: {brandStyle.name}</div>
          )}
          {uploadedUrl && (
            <div className="mt-2 text-xs text-green-600">Uploaded: <a href={uploadedUrl} target="_blank" rel="noopener noreferrer" className="underline">{uploadedUrl}</a></div>
          )}
        </div>
        <button
          type="submit"
          disabled={uploading}
          className="w-full bg-primary text-white px-6 py-3 rounded-md font-semibold hover:bg-primary/90 transition duration-300"
        >
          {uploading ? 'Uploading...' : 'Create Brand'}
        </button>
      </form>
    </div>
  );
};

export default CreateBrand; 